/**
 * PDF Export utility for reports using html2canvas + jsPDF
 * Lazy-loads libraries on demand to minimize bundle impact
 */

const LOGO_PATH = '/logo-azul-verde.png';
const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 12;
const HEADER_HEIGHT_MM = 20;
const FOOTER_HEIGHT_MM = 12;
const CONTENT_WIDTH_MM = A4_WIDTH_MM - (MARGIN_MM * 2);
// Content area: starts after header+margin, ends before footer+margin
const CONTENT_START_Y = MARGIN_MM + HEADER_HEIGHT_MM;
const CONTENT_END_Y = A4_HEIGHT_MM - MARGIN_MM - FOOTER_HEIGHT_MM;
const CONTENT_HEIGHT_MM = CONTENT_END_Y - CONTENT_START_Y;

let cachedLogo = null;
let cachedLibraries = null;

/**
 * Loads the logo image and returns an object with dataUrl and dimensions (cached)
 */
async function loadLogo() {
  if (cachedLogo) return cachedLogo;

  try {
    const response = await fetch(LOGO_PATH);
    const blob = await response.blob();

    // Load as data URL
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    // Get image dimensions
    const dimensions = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => resolve({ width: 300, height: 100 }); // fallback
      img.src = dataUrl;
    });

    cachedLogo = {
      dataUrl,
      width: dimensions.width,
      height: dimensions.height,
      aspectRatio: dimensions.width / dimensions.height
    };

    return cachedLogo;
  } catch (error) {
    console.warn('Could not load logo:', error);
    return null;
  }
}

/**
 * Lazy loads and caches the PDF libraries
 */
async function loadLibraries() {
  if (cachedLibraries) return cachedLibraries;

  const [html2canvasModule, jsPDFModule] = await Promise.all([
    import('html2canvas'),
    import('jspdf')
  ]);

  cachedLibraries = {
    html2canvas: html2canvasModule.default,
    jsPDF: jsPDFModule.jsPDF
  };

  return cachedLibraries;
}

/**
 * Adds header with logo, title, and period to each page
 */
function addHeader(pdf, logo, title, period, pageWidth) {
  const startX = MARGIN_MM;
  const startY = MARGIN_MM;

  let titleX = startX;

  // Add logo if available
  if (logo && logo.dataUrl) {
    const logoHeight = 10;
    const logoWidth = logoHeight * logo.aspectRatio;
    pdf.addImage(logo.dataUrl, 'PNG', startX, startY - 1, logoWidth, logoHeight);
    titleX = startX + logoWidth + 5;
  }

  // Add title
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(40, 72, 101);
  pdf.text(title, titleX, startY + 4);

  // Add period below title
  if (period) {
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text(period, titleX, startY + 10);
  }

  // Add separator line
  pdf.setDrawColor(40, 72, 101);
  pdf.setLineWidth(0.3);
  pdf.line(startX, startY + 14, pageWidth - MARGIN_MM, startY + 14);
}

/**
 * Adds footer with generation date and page number
 */
function addFooter(pdf, pageNumber, totalPages, pageWidth, pageHeight) {
  const footerY = pageHeight - MARGIN_MM;

  // Draw a light separator line above footer
  pdf.setDrawColor(200, 200, 200);
  pdf.setLineWidth(0.2);
  pdf.line(MARGIN_MM, footerY - 6, pageWidth - MARGIN_MM, footerY - 6);

  const now = new Date();
  const dateStr = now.toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(120, 120, 120);
  pdf.text(`Generado: ${dateStr}`, MARGIN_MM, footerY);

  const pageText = `Página ${pageNumber} de ${totalPages}`;
  const textWidth = pdf.getTextWidth(pageText);
  pdf.text(pageText, pageWidth - MARGIN_MM - textWidth, footerY);
}

/**
 * Captures a DOM element to canvas
 */
async function captureElement(element, html2canvas) {
  // Hide elements marked for exclusion
  const hiddenElements = element.querySelectorAll('[data-pdf-hide]');
  hiddenElements.forEach(el => {
    el.dataset.originalDisplay = el.style.display;
    el.style.display = 'none';
  });

  // Add PDF export class
  element.classList.add('pdf-export-mode');

  // Wait for styles to apply and animations to be disabled
  await new Promise(resolve => setTimeout(resolve, 150));

  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: 1100,
    });
    return canvas;
  } finally {
    // Restore hidden elements
    hiddenElements.forEach(el => {
      el.style.display = el.dataset.originalDisplay || '';
      delete el.dataset.originalDisplay;
    });
    element.classList.remove('pdf-export-mode');
  }
}

/**
 * Adds a canvas to the PDF, handling multi-page content
 * Returns the number of pages added
 */
function addCanvasToPDF(pdf, canvas, logo, title, period, startPage) {
  const imgWidth = CONTENT_WIDTH_MM;
  const scaleFactor = imgWidth / canvas.width;
  const totalImgHeight = canvas.height * scaleFactor;

  // Single page case - content fits within available height
  if (totalImgHeight <= CONTENT_HEIGHT_MM) {
    if (startPage > 1) {
      pdf.addPage();
    }
    addHeader(pdf, logo, title, period, A4_WIDTH_MM);
    pdf.addImage(
      canvas.toDataURL('image/png'),
      'PNG',
      MARGIN_MM,
      CONTENT_START_Y,
      imgWidth,
      totalImgHeight
    );
    return 1;
  }

  // Multi-page case - need to split content across pages
  // Calculate how much of the source canvas fits on each page
  const pageHeightPx = CONTENT_HEIGHT_MM / scaleFactor;
  const totalPages = Math.ceil(canvas.height / pageHeightPx);

  for (let page = 0; page < totalPages; page++) {
    if (startPage > 1 || page > 0) {
      pdf.addPage();
    }

    addHeader(pdf, logo, title, period, A4_WIDTH_MM);

    // Calculate source region for this page
    const sourceY = page * pageHeightPx;
    const sourceHeight = Math.min(pageHeightPx, canvas.height - sourceY);

    // Create a temporary canvas for this page's portion
    const pageCanvas = document.createElement('canvas');
    pageCanvas.width = canvas.width;
    pageCanvas.height = Math.ceil(sourceHeight);

    const ctx = pageCanvas.getContext('2d');
    ctx.drawImage(
      canvas,
      0, Math.floor(sourceY), canvas.width, Math.ceil(sourceHeight),
      0, 0, canvas.width, Math.ceil(sourceHeight)
    );

    // Calculate the height this portion will take in the PDF
    const destHeight = Math.min(sourceHeight * scaleFactor, CONTENT_HEIGHT_MM);

    // Add the image slice to PDF
    pdf.addImage(
      pageCanvas.toDataURL('image/png'),
      'PNG',
      MARGIN_MM,
      CONTENT_START_Y,
      imgWidth,
      destHeight
    );
  }

  return totalPages;
}

/**
 * Exports a single DOM element as PDF
 */
export async function exportToPDF(element, { title, period, filename }) {
  const { html2canvas, jsPDF } = await loadLibraries();
  const logo = await loadLogo();

  const canvas = await captureElement(element, html2canvas);

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pagesAdded = addCanvasToPDF(pdf, canvas, logo, title, period, 1);

  // Add page numbers
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    addFooter(pdf, i, totalPages, A4_WIDTH_MM, A4_HEIGHT_MM);
  }

  pdf.save(filename || `${title.replace(/\s+/g, '_')}.pdf`);
  return { success: true, pages: pagesAdded };
}

/**
 * Creates a new PDF document for multi-report export
 */
export async function createPDFDocument() {
  const { html2canvas, jsPDF } = await loadLibraries();
  const logo = await loadLogo();

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  let currentPage = 1;
  let isFirstReport = true;

  return {
    /**
     * Adds a report to the PDF document
     */
    async addReport(element, { title, period }) {
      const canvas = await captureElement(element, html2canvas);

      // For reports after the first, start on a new page
      if (!isFirstReport) {
        pdf.addPage();
        currentPage++;
      }
      isFirstReport = false;

      const pagesAdded = addCanvasToPDF(pdf, canvas, logo, title, period, currentPage);
      currentPage += pagesAdded - 1;

      return pagesAdded;
    },

    /**
     * Finalizes and saves the PDF
     */
    save(filename) {
      // Add page numbers to all pages
      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        addFooter(pdf, i, totalPages, A4_WIDTH_MM, A4_HEIGHT_MM);
      }

      pdf.save(filename);
      return { success: true, pages: totalPages };
    },

    /**
     * Returns the current page count
     */
    getPageCount() {
      return pdf.getNumberOfPages();
    }
  };
}

/**
 * Gets a formatted period string
 */
export function formatPeriod(year, month = null) {
  if (month !== null && month !== undefined) {
    const monthNames = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return `${monthNames[month]} ${year}`;
  }
  return `Año ${year}`;
}
